import { client } from '../default.client';
import type { ApiProxyResponse } from '../types/interfaces';
import type { ICourier } from '@/types/interfaces/chocofood';
import type {
	TariffsPayload,
	TariffsReservePayload,
	IDeliveryTariff,
	DeliveryProviderContact
} from '@/types/interfaces/delivery';
import { DELIVERY_TARIFFS } from '@/types/enums/delivery';

interface Response {
	latitude: string;
	longitude: string;
	name: string;
	phone: string;
	dropoff?: {
		min_eta: string;
		max_eta: string;
	};
	handshake_code?: string;
}

const adapter = (response: ApiProxyResponse<Response>): ICourier => {
	const { latitude, longitude, name, phone, dropoff, handshake_code } =
		response.data;

	return {
		id: 1,
		title: name,
		phone: phone,
		latitude: +latitude,
		longitude: +longitude,
		dropoff: dropoff || undefined,
		handshakeCode: handshake_code || undefined
	};
};

const getMostRelevantTariff = (response: IDeliveryTariff[]) => {
	const fastest = response.filter(tariff => tariff.is_fastest);
	return fastest.length ? fastest : response;
};

export const delivery = {
	async getDeliveryTariffs(
		payload: TariffsPayload
	): Promise<IDeliveryTariff[]> {
		const response = await client.post('dr-delivery/api/v1/tariffs', payload);
		return getMostRelevantTariff(response.data.data).sort(
			a => +(a.transport_type === DELIVERY_TARIFFS.FOOT)
		);
	},
	async reserveDeliveryTariff(
		payload: TariffsReservePayload
	): Promise<IDeliveryTariff | null> {
		const response = await client.post(
			'dr-delivery/api/v1/tariffs/reserve',
			payload
		);
		return response.data.data;
	},
	async getCourier(orderId: number): Promise<ICourier> {
		const response = await client.get('dr-delivery/api/v1/courier', {
			params: {
				takeaway_order_id: orderId
			}
		});

		return adapter(response.data);
	},
	/**
	 * Fetch vendor courier location and contact info from courier-ops.
	 * @param orderId - Takeaway order ID
	 * @returns ICourier when courier has coordinates, null otherwise
	 */
	async getVendorCourier(orderId: number): Promise<ICourier | null> {
		const response = await client.get('courier-ops/api/v1/delivery/courier', {
			params: {
				takeaway_order_id: orderId
			}
		});

		if (!response.data.data) return null;

		return adapter(response.data);
	},
	async getProviderContacts(
		filialId: number,
		provider: string
	): Promise<DeliveryProviderContact[]> {
		try {
			const response = await client.get(
				'dr-delivery/api/v1/contacts/by-terminal',
				{
					params: {
						terminal_id: filialId,
						provider
					}
				}
			);
			return response.data.data || [];
		} catch {
			return [];
		}
	}
};
